/** Datos de empresa para el PDF comercial + condiciones comerciales predeterminadas (admin). */
import React, { useState, useEffect } from 'react';
import api from '@/api/axios';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, X, UploadSimple } from '@phosphor-icons/react';

const CAMPOS = [
  ['nombre_comercial', 'Nombre comercial'], ['slogan', 'Slogan'], ['nombre_legal', 'Nombre legal'],
  ['direccion', 'Dirección'], ['telefono', 'Teléfono'], ['whatsapp', 'WhatsApp'],
  ['correo', 'Correo'], ['sitio_web', 'Sitio web'], ['nit', 'NIT'],
  ['otros', 'Otros datos comerciales'],
];

export const EmpresaConfig = ({ onClose }) => {
  const [f, setF] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/sa/empresa').then((r) => setF(r.data)).catch(() => toast.error('Error al cargar datos de empresa'));
  }, []);

  const subirLogo = async (file) => {
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/produccion/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setF((prev) => ({ ...prev, logo_url: r.data.url }));
      toast.success('Logo subido — recuerda guardar');
    } catch (e) { toast.error('No se pudo subir el logo'); }
    finally { setSubiendo(false); }
  };

  const setCampo = (k, val) => setF((prev) => ({ ...prev, [k]: val }));

  const setCond = (i, campo, val) => {
    setF((prev) => {
      const arr = [...(prev.condiciones_default || [])];
      arr[i] = { ...arr[i], [campo]: val };
      return { ...prev, condiciones_default: arr };
    });
  };

  const guardar = async () => {
    setSaving(true);
    try {
      await api.put('/sa/empresa', f);
      toast.success('Datos de empresa guardados');
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo guardar'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Datos de empresa (PDF comercial)</DialogTitle></DialogHeader>
        {!f ? <p className="text-sm text-[var(--text-muted)]">Cargando…</p> : (
          <div className="space-y-3">
            <p className="text-[11px] text-[var(--text-muted)]">
              Estos datos aparecen en el encabezado del PDF. Los campos vacíos NO se muestran (sin placeholders inventados).
            </p>
            <div className="grid grid-cols-2 gap-2">
              {CAMPOS.map(([k, label]) => (
                <div key={k} className={k === 'otros' ? 'col-span-2' : ''}>
                  <label className="text-xs font-bold text-[var(--text-muted)]">{label}</label>
                  <Input value={f[k] || ''} onChange={(e) => setCampo(k, e.target.value)} data-testid={`emp-${k}`} className="h-9" />
                </div>
              ))}
            </div>
            <div>
              <label className="text-xs font-bold text-[var(--text-muted)]">Logo</label>
              <div className="flex items-center gap-2 mt-1">
                {f.logo_url ? (
                  <img src={f.logo_url} alt="logo" className="h-10 rounded border border-[var(--border)]" data-testid="emp-logo-preview" />
                ) : (
                  <span className="text-[11px] text-[var(--text-muted)]">Sin logo propio — el PDF usa el logo oficial del CRM.</span>
                )}
                <label className="cursor-pointer">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0EA5E9] border border-[#0EA5E9]/40 rounded-md px-2 py-1.5">
                    <UploadSimple size={13} /> {subiendo ? 'Subiendo…' : 'Subir logo'}
                  </span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => subirLogo(e.target.files?.[0])} data-testid="emp-logo-file" />
                </label>
                {f.logo_url && (
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-red-400" onClick={() => setCampo('logo_url', '')} data-testid="emp-logo-quitar">Quitar</Button>
                )}
              </div>
            </div>
            <div className="border-t border-[var(--border)] pt-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-black uppercase tracking-wide text-[var(--text-muted)]">Condiciones comerciales predeterminadas</p>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs"
                  onClick={() => setF((prev) => ({ ...prev, condiciones_default: [...(prev.condiciones_default || []), { titulo: '', texto: '' }] }))}
                  data-testid="emp-add-cond">
                  <Plus size={13} className="mr-0.5" /> Condición
                </Button>
              </div>
              <p className="text-[11px] text-[var(--text-muted)]">Se copian a los presupuestos como base (vigencia, entrega, anticipo, garantía…). Cada presupuesto puede modificarlas.</p>
              <div className="space-y-2 mt-1.5">
                {(f.condiciones_default || []).map((c, i) => (
                  <div key={i} className="flex gap-1.5 items-start">
                    <Input value={c.titulo || ''} onChange={(e) => setCond(i, 'titulo', e.target.value)} placeholder="Título (ej. Vigencia)"
                      className="h-8 text-xs w-[150px]" data-testid={`emp-cond-titulo-${i}`} />
                    <Textarea rows={1} value={c.texto || ''} onChange={(e) => setCond(i, 'texto', e.target.value)} placeholder="Texto de la condición"
                      className="text-xs min-h-[32px] flex-1" data-testid={`emp-cond-texto-${i}`} />
                    <button type="button" className="text-red-400 mt-2"
                      onClick={() => setF((prev) => ({ ...prev, condiciones_default: prev.condiciones_default.filter((_, j) => j !== i) }))}
                      data-testid={`emp-cond-del-${i}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <Button disabled={saving} onClick={guardar} className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white" data-testid="emp-guardar">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
